/**
 * AudioManager - Procedural sound effects via the Web Audio API.
 *
 * The game ships no audio asset files; every sound is synthesized at runtime
 * from short oscillator envelopes. This keeps the bundle tiny and avoids
 * licensing concerns while still giving tactile feedback. A single shared
 * AudioContext is created lazily on the first user gesture (required by mobile
 * browser autoplay policies).
 */

type SfxName =
  | 'move'
  | 'capture'
  | 'check'
  | 'victory'
  | 'defeat'
  | 'click'
  | 'coin'
  | 'upgrade'
  | 'error'
  | 'smack';

const SINGLETON_KEY = '__chessrogue_audio__';
const SETTINGS_KEY = 'chessrogue_settings';

export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private soundOn = true;
  private musicOn = true;
  private initialized = false;

  // Ambient music state
  private musicGain: GainNode | null = null;
  private musicVoices: OscillatorNode[] = [];
  private musicTimer: number | null = null;
  private chordIndex = 0;

  static getInstance(): AudioManager {
    const g = globalThis as any;
    if (!g[SINGLETON_KEY]) {
      g[SINGLETON_KEY] = new AudioManager();
    }
    return g[SINGLETON_KEY];
  }

  private hapticsOn = true;

  private constructor() {
    this.soundOn = this.loadPreference('soundOn');
    this.musicOn = this.loadPreference('musicOn');
    this.hapticsOn = this.loadPreference('hapticsOn');
  }

  isHapticsOn(): boolean {
    return this.hapticsOn;
  }

  setHapticsOn(on: boolean): void {
    this.hapticsOn = on;
    this.savePreference('hapticsOn', on);
    if (on) this.haptic(15);
  }

  /**
   * Lightweight haptic feedback via the Vibration API (Android/Capacitor
   * WebView). A no-op where unsupported (e.g. desktop, iOS Safari) and when
   * disabled — purely additive tactile polish on mobile.
   */
  haptic(pattern: number | number[]): void {
    if (!this.hapticsOn) return;
    try {
      const nav = navigator as any;
      if (nav && typeof nav.vibrate === 'function') nav.vibrate(pattern);
    } catch {
      // ignore
    }
  }

  /**
   * Wire up an AudioContext. Safe to call repeatedly; the context resumes on
   * the next user gesture if a browser suspended it.
   */
  init(scene?: Phaser.Scene): void {
    if (!this.initialized) {
      try {
        const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (Ctor) {
          this.ctx = new Ctor();
          this.masterGain = this.ctx!.createGain();
          this.masterGain.gain.value = 0.35;
          this.masterGain.connect(this.ctx!.destination);
        }
      } catch {
        this.ctx = null;
      }
      this.initialized = true;
    }

    // Resume the context (and start music) on the first input (autoplay policy)
    if (scene && this.ctx) {
      const resume = () => {
        this.ctx?.resume().catch(() => {});
        if (this.musicOn) this.startMusic();
      };
      if (this.ctx.state === 'suspended') {
        scene.input.once('pointerdown', resume);
      } else if (this.musicOn) {
        this.startMusic();
      }
    }
  }

  isSoundOn(): boolean {
    return this.soundOn;
  }

  setSoundOn(on: boolean): void {
    this.soundOn = on;
    this.savePreference('soundOn', on);
  }

  isMusicOn(): boolean {
    return this.musicOn;
  }

  setMusicOn(on: boolean): void {
    this.musicOn = on;
    this.savePreference('musicOn', on);
    if (on) this.startMusic();
    else this.stopMusic();
  }

  private savePreference(key: string, value: boolean): void {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      const settings = raw ? JSON.parse(raw) : {};
      settings[key] = value;
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // ignore persistence failures
    }
  }

  private loadPreference(key: string): boolean {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw) {
        const settings = JSON.parse(raw);
        if (typeof settings[key] === 'boolean') return settings[key];
      }
    } catch {
      // ignore
    }
    return true;
  }

  // ---- Ambient music: a slow, low chord pad that drifts through a minor
  // progression. Intentionally quiet and unobtrusive. ----

  private startMusic(): void {
    if (!this.ctx || !this.musicOn || this.musicGain) return;
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.0;
    this.musicGain.connect(this.ctx.destination);
    // gentle fade-in
    this.musicGain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
    this.musicGain.gain.linearRampToValueAtTime(0.05, this.ctx.currentTime + 4);
    this.chordIndex = 0;
    this.playChord();
    this.musicTimer = window.setInterval(() => this.playChord(), 7000);
  }

  private stopMusic(): void {
    if (this.musicTimer !== null) {
      clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.musicGain && this.ctx) {
      const t = this.ctx.currentTime;
      this.musicGain.gain.cancelScheduledValues(t);
      this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, t);
      this.musicGain.gain.linearRampToValueAtTime(0.0001, t + 1.5);
    }
    const gainToClose = this.musicGain;
    setTimeout(() => {
      this.musicVoices.forEach(v => { try { v.stop(); } catch { /* already stopped */ } });
      this.musicVoices = [];
      try { gainToClose?.disconnect(); } catch { /* ignore */ }
    }, 1600);
    this.musicGain = null;
  }

  private playChord(): void {
    if (!this.ctx || !this.musicGain) return;
    // Minor-key pad progression (root frequencies, two octaves of a triad)
    const chords = [
      [110.0, 130.81, 164.81], // A minor
      [87.31, 110.0, 130.81],  // F major
      [98.0, 123.47, 146.83],  // G major
      [82.41, 98.0, 123.47],   // E minor
    ];
    const chord = chords[this.chordIndex % chords.length];
    this.chordIndex++;

    // Stop the previous chord's voices with a short release
    const t0 = this.ctx.currentTime;
    this.musicVoices.forEach(v => { try { v.stop(t0 + 2.5); } catch { /* ignore */ } });
    this.musicVoices = [];

    for (const freq of chord) {
      for (const mult of [1, 2]) {
        const osc = this.ctx.createOscillator();
        const env = this.ctx.createGain();
        osc.type = mult === 1 ? 'sine' : 'triangle';
        osc.frequency.value = freq * mult;
        osc.detune.value = (Math.sin(freq) * 6); // slight, deterministic detune
        env.gain.setValueAtTime(0.0001, t0);
        env.gain.linearRampToValueAtTime(mult === 1 ? 0.5 : 0.18, t0 + 2.5);
        env.gain.linearRampToValueAtTime(0.0001, t0 + 7.5);
        osc.connect(env);
        env.connect(this.musicGain);
        osc.start(t0);
        osc.stop(t0 + 8);
        this.musicVoices.push(osc);
      }
    }
  }

  /**
   * Play a named sound effect. No-op when muted or audio is unavailable.
   */
  play(name: SfxName): void {
    if (!this.soundOn || !this.ctx || !this.masterGain) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }

    switch (name) {
      case 'move':
        this.blip(220, 0.06, 'triangle', 0.5);
        break;
      case 'capture':
        this.noiseHit(0.14);
        this.blip(140, 0.1, 'square', 0.4);
        break;
      case 'check':
        this.blip(660, 0.12, 'sawtooth', 0.4);
        this.blip(880, 0.12, 'sawtooth', 0.3, 0.06);
        break;
      case 'victory':
        this.arpeggio([523, 659, 784, 1047], 0.12);
        break;
      case 'defeat':
        this.arpeggio([392, 330, 262, 196], 0.16, 'sawtooth');
        break;
      case 'click':
        this.blip(440, 0.04, 'square', 0.3);
        break;
      case 'coin':
        this.blip(988, 0.05, 'square', 0.35);
        this.blip(1319, 0.08, 'square', 0.3, 0.04);
        break;
      case 'upgrade':
        this.arpeggio([523, 784, 1047], 0.1, 'triangle');
        break;
      case 'error':
        this.blip(160, 0.18, 'sawtooth', 0.4);
        break;
      case 'smack':
        // Dramatic impact: a sharp noise crack over a punchy low thud
        this.noiseHit(0.09);
        this.thud(170, 70, 0.22);
        this.blip(90, 0.18, 'square', 0.5);
        break;
    }
  }

  /** A short pitch-dropping thud for impact sounds. */
  private thud(startFreq: number, endFreq: number, duration: number): void {
    if (!this.ctx || !this.masterGain) return;
    const t0 = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, t0);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), t0 + duration);
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(0.6, t0 + 0.01);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(env);
    env.connect(this.masterGain);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  }

  private blip(
    freq: number,
    duration: number,
    type: OscillatorType,
    gain: number,
    delay = 0
  ): void {
    if (!this.ctx || !this.masterGain) return;
    const t0 = this.ctx.currentTime + delay;
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(env);
    env.connect(this.masterGain);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  }

  private arpeggio(freqs: number[], step: number, type: OscillatorType = 'square'): void {
    freqs.forEach((f, i) => this.blip(f, step * 1.4, type, 0.32, i * step));
  }

  private noiseHit(duration: number): void {
    if (!this.ctx || !this.masterGain) return;
    const t0 = this.ctx.currentTime;
    const bufferSize = Math.floor(this.ctx.sampleRate * duration);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const env = this.ctx.createGain();
    env.gain.value = 0.3;
    src.connect(env);
    env.connect(this.masterGain);
    src.start(t0);
  }
}
